// EnergyV2 — Energy's rebuilt screen (UI rebuild batch 4: the UI-rebuild lane's hand-off of 27 Oct 2026, ui-rebuild/batch4-handoff
// 3eb850a, spec docs/ui-rebuild/handoff/ENERGY.md there — adopted by the energy lane the same day; drawn only when lib/ui-v2.ts
// switches batch 4 on). Three pages:
//
//   /energy                       the list: what needs him first, then the wallets (Astra's `energy`)
//   /energy/rent?wallet=<id>      Rent energy: wallet, energy, term → the price → the order (Astra's `rent`)
//   /energy/settings/<id>         the wallet's standing order, label and status (Astra's `energy-settings`)
//
// REDESIGNED 30 Oct 2026 (his words: "yes redesign properly- and label clearly"; he could not tell where TRX goes, or which
// wallets need energy): the TronSave balance first, in a box of its own — the figure, its state (not read / low / below the
// minimum) and the deposit address in full under "Send TRX here", with Copy (BalanceBox). Then one register of the rentals that
// need him — a rental with no answer ("may be charged"), one the provider took and has not filled, one delivered and not yet
// on the wallet — and the ACTIVE wallets, each saying what it needs ("needs 56,800 energy"), the ones that need him first; the
// paused and retired fold into one line under them. The gold button is "Rent energy". The limits, the prices and the screen's
// whole explanation are behind Details (the desk sheet). A row opens its sheet: a rental's has the settle buttons; a wallet's
// has Rent energy and Settings. Quiet rental history
// is behind "All rentals". Add a wallet is one quiet link (full access).
//
// ONE FACT PER CELL (his words, 1 Nov 2026: "Amount, chain, date and time everything in 1st column- doesn't it look wrong", "check
// everything where it applies", "check all the cards and button placements"): the first column is a row's name and what identifies
// it — a wallet's address, a rental's wallet — and nothing else; Available and Needs are figures with their unit, or "—", never a
// state (the tag says that); paused or retired is a tag beside the name; a rental's energy, term, TRX and when it was sent are
// columns of their own; a phone card says each of those as a labelled line; a line that warns is in its box; Refresh stays at the
// top right on the phone.
//
// ONE COPY: every rule, word and line is screens/Energy.tsx's, IMPORTED — the one read (useDesk), what needs him (useNeeds and its
// lines: an order with no answer red until the desk's list says what became of it, "Noted" only once the list has been read
// since, the alarms said elsewhere left out), the "is there a picture, and is it current?" lines (ReadLines: a first read opened
// offline is waiting, never "couldn't read"), the register's no, a Settle link to the rental an alarm NAMES, a wallet's state
// in one word (walletSeat). What is this screen's own: the register's rows and columns, which alarms a row carries, the sheets.
import { useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, RefreshCw } from 'lucide-react';
import type { EnergyAlarm, EnergyRental, EnergyStatus, EnergyWallet } from '../../../lib/api';
import { fmtRelative, fmtStamp } from '../../../lib/format';
import { alarmAbout, alarmNamesRental, alarmRentalIds, rentalNeedsHim } from '../../../lib/energyOrder';
import {
  EmptyState, PageHead, Panel, Register, SearchBar, SkeletonRows, StateLine, TAP, type RegisterColumn,
} from '../../../components/v2/kit';
import { Pill } from '../../../components/Pill';
import { DeskLines, EnergyFigure, EnergyNeed, EnergyNow, FactList, LifeTag, TrxFigure, V2_SLABS } from '../../../components/v2/energy/parts';
import { RentalSheet } from '../../../components/v2/energy/RentalSheet';
import { WalletSheet } from '../../../components/v2/energy/WalletSheet';
import { DeskSheet } from '../../../components/v2/energy/DeskSheet';
import { AddWalletSheet } from '../../../components/v2/energy/AddWalletSheet';
import { BalanceBox } from '../../../components/v2/energy/BalanceBox';
import {
  NeedsLines, capital, firstRead, lasts, mask, orders, readWords, registerUnreadable,
  listed, rentalSeat, rentalTrx, units, useDesk, useNeeds, walletName, walletSeat, nameOf, namedWallets, alarmConcernsWallet, balanceState
} from '../../Energy';

type Show = 'wallets' | 'needs' | 'rentals';
const SHOWS: Array<{ key: Show; label: string }> = [
  { key: 'wallets', label: 'Wallets' },
  { key: 'needs', label: 'Needs you' },
  { key: 'rentals', label: 'All rentals' },
];
type Row = { kind: 'wallet'; w: EnergyWallet } | { kind: 'rental'; r: EnergyRental };
const rowKey = (x: Row) => (x.kind === 'wallet' ? `w${x.w.id}` : `r${x.r.id}`);

/** Alarms about the rows themselves — a rental, a wallet — which a row can carry; the rest are the desk's own (balance, burn, spend, rounds). */
const ROW_ALARMS = new Set(['unconfirmed', 'undelivered', 'unseen', 'standing', 'dormant', 'chain']);
const RANK = { neutral: 0, info: 0, gold: 0, good: 0, warn: 1, bad: 2 } as const;

export function EnergyV2() {
  const desk = useDesk();
  const { q, d, canChange, fresh, refresh } = desk;
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams();
  const openedHere = useRef(false);
  const show: Show = SHOWS.find((s) => s.key === search.get('show'))?.key ?? 'wallets';
  const needle = (search.get('q') ?? '').trim().toLowerCase();

  /** One URL change: `push` for opening a sheet (Back closes it), replace for a filter. */
  const set = (patch: Record<string, string | null>, push = false) => {
    const next = new URLSearchParams(search);
    for (const [k, v] of Object.entries(patch)) { if (v === null) next.delete(k); else next.set(k, v); }
    setSearch(next, { replace: !push });
  };
  const openSheet = (patch: Record<string, string>) => { openedHere.current = true; set(patch, true); };
  const closeSheet = (keys: string[]) => {
    if (openedHere.current) { openedHere.current = false; navigate(-1); return; }
    set(Object.fromEntries(keys.map((k) => [k, null])));
  };

  // ── What needs him: Energy.tsx's — the alarms not said elsewhere, the orders that got no answer, the rentals the lines name ──
  const needs = useNeeds(desk);
  const { level, alarms, lostRed, lostAmber, foundRed, foundAmber, jumpWord, linked } = needs;
  const first = firstRead(q);

  // ── The register's rows: the rentals that need him first, then the wallets, the ones that need him first ──
  const needsRental = (r: EnergyRental) => rentalNeedsHim(r) || linked.has(r.id);
  const openRentals = d ? d.rentals.filter(needsRental) : [];
  const seatOf = (w: EnergyWallet) => walletSeat(d as EnergyStatus, w);
  const [foldOpen, setFoldOpen] = useState(false);
  const { shown, folded, foldWords } = listed(d?.wallets ?? []);
  const wallets = [...shown, ...(foldOpen ? folded : [])].sort((a, b) => Number(seatOf(b).needs) - Number(seatOf(a).needs) || Number(a.status !== 'active') - Number(b.status !== 'active'));
  const toneOf = (x: Row) => (x.kind === 'rental' ? rentalSeat(x.r).tone : seatOf(x.w).tone);   // the row's own tag
  /**
   * v2: an alarm ABOUT rows (a rental, a wallet) that the desk names by id, every one of them a row of the register in at least the
   * alarm's colour, is said by those rows — one failure, one message (Astra) — and its whole sentence is in each row's sheet.
   * Anything else stays a line: the desk's own alarms, an alarm that names nothing, a row that would say it more quietly.
   */
  const defaultRows: Row[] = [...openRentals.map((r) => ({ kind: 'rental' as const, r })), ...wallets.map((w) => ({ kind: 'wallet' as const, w }))];
  const carried = (a: EnergyAlarm): boolean => {
    // The TronSave balance box says it — the figure, Low or Below minimum, Not read, in its tone — but not a desk with no key, nor a
    // balance never read: there the box shows a figure or a grey "Not read yet" while the desk will not order, so the alarm stays a
    // line (Astra, 2 Nov 2026).
    if (a.key === 'float') return d !== undefined && d.provider.canOrder && balanceState(d.provider).key !== 'waiting';
    // A red one — an order that got no answer and may have been charged, the desk blind — is its own boxed line at the top, with
    // its button (1 Nov 2026: a row's tag says the state; the warning and its way out are a line of their own).
    if (a.level === 'critical') return false;
    if (!d || !ROW_ALARMS.has(a.key)) return false;
    const need = RANK.warn;
    const rIds = alarmRentalIds(a);
    const wIds = alarmAbout(a, namedWallets(d))?.wallets.map((w) => w.id) ?? [];
    const named: Row[] = [];
    if (rIds === null) return false;
    for (const id of rIds ?? []) { const r = openRentals.find((x) => x.id === id); if (!r) return false; named.push({ kind: 'rental', r }); }
    if (rIds === undefined || rIds.length === 0) {
      if (wIds.length === 0) return false;
      for (const id of wIds) { const w = wallets.find((x) => x.id === id); if (!w || !seatOf(w).needs) return false; named.push({ kind: 'wallet', w }); }
    }
    return named.length > 0 && named.every((x) => RANK[toneOf(x)] >= need);
  };
  const lines = alarms.filter((a) => !carried(a));
  const critical = lines.filter((a) => a.level === 'critical');
  const warnings = lines.filter((a) => a.level !== 'critical');
  const alarmsAbout = (x: Row): EnergyAlarm[] => alarms.filter((a) => (x.kind === 'rental'
    ? alarmNamesRental(a, x.r.id)
    : d !== undefined && alarmConcernsWallet(a, d, x.w.id)));

  const pick: Row[] = show === 'rentals'
    ? (d ? [...openRentals, ...d.rentals.filter((r) => !needsRental(r))].map((r) => ({ kind: 'rental' as const, r })) : [])
    : show === 'needs' ? defaultRows.filter((x) => x.kind === 'rental' || seatOf(x.w).needs) : defaultRows;
  const matches = (x: Row) => !needle || (x.kind === 'wallet'
    ? [nameOf(d, x.w), x.w.label, x.w.address, units(x.w.available), x.w.available ?? ''].some((s) => s.toLowerCase().includes(needle))
    : [`#${x.r.id}`, String(x.r.id), walletName(d as EnergyStatus, x.r), x.r.receiver, units(x.r.energy), x.r.energy, rentalTrx(x.r).text].some((s) => s.toLowerCase().includes(needle)));
  const rows = pick.filter(matches);

  const shownRental = d && search.get('rental') ? d.rentals.find((r) => String(r.id) === search.get('rental')) ?? null : null;
  const shownWallet = d && search.get('wallet') ? d.wallets.find((w) => String(w.id) === search.get('wallet')) ?? null : null;
  const noted = (key: string) => <button type="button" className={TAP} onClick={() => orders.dispatch({ type: 'dismiss', key })}>Noted</button>;
  const toPanel = (walletId: number, purpose: string) => (
    <Link className={TAP} to={purpose === 'standing' ? `/energy/settings/${walletId}` : `/energy/rent?wallet=${walletId}`}>Open</Link>
  );
  const toRental = (id: number) => <button type="button" className={TAP} onClick={() => openSheet({ rental: String(id) })}>{jumpWord(id)} #{id}</button>;
  const anyLine = lostRed.length + critical.length + foundRed.length + lostAmber.length + foundAmber.length + warnings.length > 0;

  return (
    <>
      <PageHead
        title={<span className="inline-flex items-center gap-2">Energy{d?.network === 'nile' && <Pill tone="warn">Test network</Pill>}</span>}
        sub={d ? capital(readWords(d.fetched_at)) : first === 'failed' ? 'Not read' : first === 'offline' ? 'Offline' : 'Reading…'}
        inline
        actions={
          <>
            {/* Refresh stays at the top right on the phone too (the audit of 1 Nov 2026: the phone lost it), as an icon there. */}
            <button type="button" className="btn-secondary min-w-11 px-3 text-body disabled:opacity-50" disabled={q.isFetching} onClick={refresh}
              aria-label={q.isFetching ? 'Reading…' : 'Refresh'} title="Refresh">
              <RefreshCw aria-hidden className={`h-4 w-4 lg:hidden ${q.isFetching ? 'animate-spin' : ''}`} />
              <span className="hidden lg:inline">{q.isFetching ? 'Reading…' : 'Refresh'}</span>
            </button>
            <Link to="/energy/rent" className="btn-primary px-4">Rent energy</Link>
          </>
        }
      >
      </PageHead>

      <div className="mb-4 space-y-3 empty:hidden lg:mb-5">
        <DeskLines desk={desk} rows />
      </div>
      {d && <BalanceBox d={d} spendTone={level('spend') === 'warn' ? 'warn' : undefined} onDetails={() => openSheet({ desk: '1' })} />}
      <div className="mb-4 space-y-3 empty:hidden lg:mb-5">
        {d && registerUnreadable(d) && (
          <StateLine tone="warn"
            more="The Books' wallet register could not be read just now. Until it can be, no wallet is added, topped up or given a standing figure from here.">
            Books&apos; register unreadable — renting and standing figures are off.
          </StateLine>
        )}
        {/* What needs him that is not a row: the orders this screen sent and got no answer to, and the desk's own alarms. Red first. */}
        {d && anyLine && (
          <div className="space-y-2">
            <NeedsLines d={d} needs={needs} critical={critical} warnings={warnings} rentalsHere={false} open={toPanel} rental={toRental} noted={noted} slabs={V2_SLABS} />
          </div>
        )}
      </div>

      {d && d.wallets.length + d.rentals.length > 0 && (
        <SearchBar value={search.get('q') ?? ''} onChange={(v) => set({ q: v || null })} placeholder="Search"
          active={show !== 'wallets' ? 1 : 0}
          filters={
            <div className="flex flex-wrap gap-2" role="group" aria-label="Show">
              {SHOWS.map((s) => (
                <button key={s.key} type="button" aria-pressed={s.key === show} className={`chip ${s.key === show ? 'chip-on' : ''}`}
                  onClick={() => set({ show: s.key === 'wallets' ? null : s.key })}>{s.label}</button>
              ))}
            </div>
          } />
      )}

      {/* A first read on its way, or waiting for a connection: grey bars, never "empty" (a failed one is DeskLines' red line). */}
      {(first === 'reading' || first === 'offline') && <Panel flush><SkeletonRows what="the energy desk" /></Panel>}
      {d && (
        <Panel flush>
          {rows.length === 0 ? (
            <EmptyState>{needle ? 'No listed row matches.' : show === 'rentals' ? 'Nothing rented yet.' : show === 'needs' ? 'Nothing needs you.' : 'No wallets yet.'}</EmptyState>
          ) : (
            <Register label="Energy" rows={rows} rowKey={rowKey}
              onOpen={(x) => openSheet(x.kind === 'rental' ? { rental: String(x.r.id) } : { wallet: String(x.w.id) })}
              columns={show === 'rentals' ? rentalColumns(d) : columns(d)} card={card(d, show)} />
          )}
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-stroke-soft px-4 py-3 text-meta text-tg-hint lg:px-5">
            <span>
              {show === 'rentals'
                ? `${d.rentals.length} rentals · newest first`
                : `${shown.length} active ${shown.length === 1 ? 'wallet' : 'wallets'}${openRentals.length > 0 ? ` · ${openRentals.length} ${openRentals.length === 1 ? 'rental needs' : 'rentals need'} you` : ''}`}
              {show !== 'rentals' && foldWords && (
                <> · <button type="button" className={TAP} aria-expanded={foldOpen} onClick={() => setFoldOpen((x) => !x)}>{foldOpen ? `Hide ${foldWords}` : `+ ${foldWords}`}</button></>
              )}
            </span>
            <span className="flex flex-wrap items-center gap-x-4">
              {show !== 'rentals' && d.rentals.length > 0 && <button type="button" className={TAP} onClick={() => set({ show: 'rentals' })}>All rentals ({d.rentals.length}) →</button>}
              {canChange && (
                <button type="button" className={`${TAP} disabled:opacity-50`} disabled={registerUnreadable(d)} title={registerUnreadable(d) ? 'The Books\' register cannot be read just now' : undefined}
                  onClick={() => openSheet({ add: '1' })}>Add a wallet</button>
              )}
            </span>
          </div>
        </Panel>
      )}
      {/* The desk's list is the newest 40 rentals and every one still unsettled — said where the history is. */}
      {d && show === 'rentals' && <p className="mt-3 text-meta text-tg-hint">Newest 40, and every unsettled one.</p>}

      {d && shownRental && <RentalSheet r={shownRental} d={d} canChange={canChange} alarms={alarmsAbout({ kind: 'rental', r: shownRental })} onClose={() => closeSheet(['rental'])} />}
      {d && shownWallet && <WalletSheet w={shownWallet} d={d} canChange={canChange} alarms={alarmsAbout({ kind: 'wallet', w: shownWallet })} onClose={() => closeSheet(['wallet'])} />}
      {d && search.get('desk') === '1' && <DeskSheet d={d} onClose={() => closeSheet(['desk'])} />}
      {d && canChange && search.get('add') === '1' && <AddWalletSheet d={d} onClose={() => closeSheet(['add'])} />}
    </>
  );
}

// ── The register's row: what it is · the energy it has · what it needs · its state — one fact a cell ──
const Chevron = () => <ChevronRight className="h-4 w-4 shrink-0 text-tg-hint" aria-hidden />;
const rentalTitle = (d: EnergyStatus, r: EnergyRental) => (
  <span className="block min-w-0">
    <span className="block font-medium">Rental #{r.id}</span>
    <span className="block break-words text-meta font-normal text-tg-hint">{walletName(d, r)}</span>
  </span>
);
const walletTitle = (d: EnergyStatus | undefined, w: EnergyWallet) => (
  <span className="block min-w-0">
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1"><span className="break-words font-medium">{nameOf(d, w)}</span>{d && <LifeTag w={w} d={d} />}</span>
    <span className="block whitespace-nowrap font-mono text-meta font-normal text-tg-hint">{mask(w.address)}</span>
  </span>
);
const Tag = ({ x, d }: { x: Row; d: EnergyStatus }) => {
  const t = x.kind === 'rental' ? rentalSeat(x.r) : walletSeat(d, x.w);
  return <Pill tone={t.tone}>{t.word}</Pill>;
};
/** When a rental was sent: a Dubai time ("08:40" today, "26 Oct 08:40" before), how long ago on hover. */
const Sent = ({ r }: { r: EnergyRental }) => <span className="whitespace-nowrap" title={fmtRelative(r.createdAt)}>{fmtStamp(r.createdAt)}</span>;
const Trx = ({ r }: { r: EnergyRental }) => <TrxFigure value={rentalTrx(r).text} title={rentalTrx(r).tip} />;

function columns(d: EnergyStatus): ReadonlyArray<RegisterColumn<Row>> {
  return [
    { key: 'what', head: 'Wallet / rental', cell: (x) => (x.kind === 'rental' ? rentalTitle(d, x.r) : walletTitle(d, x.w)) },
    { key: 'available', head: 'Available', align: 'right', width: 'w-[20%]', cell: (x) => (x.kind === 'wallet' ? <EnergyNow w={x.w} /> : null) },
    { key: 'needs', head: 'Needs', align: 'right', width: 'w-[20%]', cell: (x) => (x.kind === 'wallet' ? <EnergyNeed w={x.w} /> : null) },
    { key: 'state', head: 'State', align: 'right', width: 'w-[200px]', cell: (x) => <span className="flex items-center justify-end gap-3"><Tag x={x} d={d} /><Chevron /></span> },
  ];
}

/** All rentals: when it was sent first (a register of what happened), then which rental, its energy, its term, its TRX, its state. */
function rentalColumns(d: EnergyStatus): ReadonlyArray<RegisterColumn<Row>> {
  return [
    { key: 'sent', head: 'Sent', width: 'w-[15%]', cell: (x) => (x.kind === 'rental' ? <Sent r={x.r} /> : null) },
    { key: 'what', head: 'Rental', cell: (x) => (x.kind === 'rental' ? rentalTitle(d, x.r) : null) },
    { key: 'energy', head: 'Energy', align: 'right', width: 'w-[16%]', cell: (x) => (x.kind === 'rental' ? <EnergyFigure n={x.r.energy} /> : null) },
    { key: 'term', head: 'Term', align: 'right', width: 'w-[11%]', cell: (x) => (x.kind === 'rental' ? <span className="whitespace-nowrap">{lasts(x.r.durationSec)}</span> : null) },
    { key: 'trx', head: 'TRX', align: 'right', width: 'w-[11%]', cell: (x) => (x.kind === 'rental' ? <Trx r={x.r} /> : null) },
    { key: 'state', head: 'State', align: 'right', width: 'w-[180px]', cell: (x) => <span className="flex items-center justify-end gap-3"><Tag x={x} d={d} /><Chevron /></span> },
  ];
}

/** Astra's phone card: what it is (and what identifies it), its state at the right — and what the laptop shows as columns, a labelled
 *  line each: a wallet's energy and what it needs; on All rentals, a rental's sent time, energy, term and TRX. */
function card(d: EnergyStatus, show: Show) {
  return (x: Row) => {
    if (x.kind === 'rental') {
      return {
        title: <>{rentalTitle(d, x.r)}{show === 'rentals' && (
          <FactList left facts={[['Sent', <Sent key="sent" r={x.r} />], ['Energy', <EnergyFigure key="energy" n={x.r.energy} />],
            ['Term', lasts(x.r.durationSec)], ['TRX', <Trx key="trx" r={x.r} />]]} />
        )}</>,
        figure: <Chevron />,
        state: <Tag x={x} d={d} />,
      };
    }
    return {
      title: <>{walletTitle(d, x.w)}<FactList left facts={[['Available', <EnergyNow key="now" w={x.w} />], ['Needs', <EnergyNeed key="need" w={x.w} />]]} /></>,
      figure: <Chevron />,
      state: <Tag x={x} d={d} />,
    };
  };
}
